import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  Switch,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  IconButton,
  Badge,
  HStack,
  VStack,
  Select,
  Textarea,
  Input,
  InputGroup,
  InputLeftElement,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import {
  EditIcon,
  DeleteIcon,
  SearchIcon,
  RepeatIcon,
  CloseIcon,
  AttachmentIcon,
  ViewIcon,
  CheckCircleIcon,
  InfoOutlineIcon,
} from "@chakra-ui/icons";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomToast } from "../../../../components";
import { useFetch, baseUrl } from "../../../../hooks/useFetch";
import CustomTables, { Column, ILazyParams } from "../../../../components/CustomTable/CustomTables";
import { MultiSelectMenu } from "../../../../components/MultiSelectMenu/MultiSelectMenu";
import { IInductionPolicy, InductionConfigType, emptyInductionPolicy, inductionPolicySchema, organizationPresets, branchPresets, businessPresets, departmentPresets, policyPresets, } from "./modal";
import MyDiv from "./InductionPolicy.style";

const SECTION_TABS: {
  type: InductionConfigType;
  label: string;
  step: string;
  description: string;
}[] = [
    {
      type: "ORGANIZATION",
      label: "Company Profile",
      step: "Step 1",
      description: "Vision, mission, company overview & core values",
    },
    {
      type: "BRANCH",
      label: "Branch & Location",
      step: "Step 2",
      description: "Branch welcome, facilities, operating hours & amenities",
    },
    {
      type: "BUSINESS",
      label: "Business Process",
      step: "Step 3",
      description: "Process overview, SLAs, operational focus & services",
    },
    {
      type: "DEPARTMENT",
      label: "Department",
      step: "Step 4",
      description: "Department objectives, tools stack, workflow & teams",
    },
    {
      type: "POLICY",
      label: "HR Policies",
      step: "Step 5",
      description: "Leave quota, attendance rules, notice period & conduct",
    },
  ];

// Website Brand Palette (used across HRMS)
const BRAND = "#006838";
const BRAND_HOVER = "#005529";
const BRAND_BG = "#E8F5EE";
const BRAND_BORDER = "#CFE5D8";

// Video upload limits
const MAX_VIDEO_MB = 100;
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/ogg"];

export default function InductionPolicy() {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [activeTabIdx, setActiveTabIdx] = useState<number>(0);
  const activeSection = SECTION_TABS[activeTabIdx];
  const activeTabType: InductionConfigType = activeSection.type;

  const [tableData, setTableData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedData, setSelectedData] = useState<any>(null);

  // Dropdown lists
  const [companies, setCompanies] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [businessProcesses, setBusinessProcesses] = useState<any[]>([]);

  // Multiselect form states
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
  const [selectedBusinessProcesses, setSelectedBusinessProcesses] = useState<number[]>([]);

  // Step video states (ONE video per step/tab)
  const [stepVideos, setStepVideos] = useState<Record<string, string>>({});
  const [videoUploading, setVideoUploading] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Pre-upload confirmation preview modal states
  const [pendingVideoFile, setPendingVideoFile] = useState<File | null>(null);
  const [pendingVideoPreviewUrl, setPendingVideoPreviewUrl] = useState<string | null>(null);
  const [isPreUploadModalOpen, setIsPreUploadModalOpen] = useState(false);
  const [videoTargetCompanies, setVideoTargetCompanies] = useState<number[]>([]);
  const [videoTargetBranches, setVideoTargetBranches] = useState<number[]>([]);
  const [videoTargetDepartments, setVideoTargetDepartments] = useState<number[]>([]);

  // Watch video modal state (modal to view video cleanly without hiding table)
  const [isWatchModalOpen, setIsWatchModalOpen] = useState(false);

  // Filter bar states
  const [filterCompany, setFilterCompany] = useState<string>("");
  const [filterBranch, setFilterBranch] = useState<string>("");
  const [filterDepartment, setFilterDepartment] = useState<string>("");
  const [filterBusinessProcess, setFilterBusinessProcess] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [lazyParams, setLazyParams] = useState<ILazyParams>({
    first: 0,
    rows: 20,
    sortField: undefined,
    sortOrder: undefined,
    multiSortMeta: [],
    filters: {},
    globalFilter: "",
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IInductionPolicy>({
    resolver: yupResolver(inductionPolicySchema) as any,
    defaultValues: emptyInductionPolicy,
    mode: "onChange",
  });

  const watchedConfigType = watch("config_Type") || activeTabType;
  const watchedPolicyKey = watch("policy_Key") || "";
  const watchedPolicyTitle = watch("policy_Title") || "";
  const watchedPolicyContent = watch("policy_Content") || "";
  const watchedOrderIndex = watch("order_Index") || 1;

  // ---------- Step video helpers (one video per step) ----------
  const loadStepVideos = async () => {
    const res = await fetchApi("InductionPolicyConfig/step-videos", "GET");
    if (res && Array.isArray(res)) {
      const map: Record<string, string> = {};
      res.forEach((v: any) => {
        if (v.config_Type && v.video_Url) map[v.config_Type] = v.video_Url;
      });
      setStepVideos(map);
    }
  };

  // Video pick handler - opens confirmation preview modal BEFORE uploading
  const handleVideoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const videoFile = e.target.files?.[0];
    e.target.value = "";
    if (!videoFile) return;

    if (!ALLOWED_VIDEO_TYPES.includes(videoFile.type)) {
      addToast({
        message: "Only MP4, WebM or OGG video formats are allowed.",
        status: "warning",
      });
      return;
    }
    if (videoFile.size > MAX_VIDEO_MB * 1024 * 1024) {
      addToast({
        message: `Video size cannot exceed ${MAX_VIDEO_MB} MB. (Selected: ${(videoFile.size / (1024 * 1024)).toFixed(1)} MB)`,
        status: "warning",
      });
      return;
    }

    if (pendingVideoPreviewUrl) {
      URL.revokeObjectURL(pendingVideoPreviewUrl);
    }

    const previewUrl = URL.createObjectURL(videoFile);
    setPendingVideoFile(videoFile);
    setPendingVideoPreviewUrl(previewUrl);
    setVideoTargetCompanies([]);
    setVideoTargetBranches([]);
    setVideoTargetDepartments([]);
    setIsPreUploadModalOpen(true);
  };

  const handleCancelPreUpload = () => {
    if (pendingVideoPreviewUrl) {
      URL.revokeObjectURL(pendingVideoPreviewUrl);
    }
    setPendingVideoFile(null);
    setPendingVideoPreviewUrl(null);
    setVideoTargetCompanies([]);
    setVideoTargetBranches([]);
    setVideoTargetDepartments([]);
    setIsPreUploadModalOpen(false);
  };

  const handleConfirmVideoUpload = async () => {
    if (!pendingVideoFile) return;

    const stepType = activeTabType;
    setVideoUploading(true);
    const formData = new FormData();
    formData.append("file", pendingVideoFile);
    if (videoTargetCompanies.length > 0) {
      formData.append("company_Ids", JSON.stringify(videoTargetCompanies));
    }
    if (videoTargetBranches.length > 0) {
      formData.append("branch_Ids", JSON.stringify(videoTargetBranches));
    }
    if (videoTargetDepartments.length > 0) {
      formData.append("department_Ids", JSON.stringify(videoTargetDepartments));
    }

    const res = await fetchApi(
      `InductionPolicyConfig/step-video/${stepType}`,
      "POST",
      formData,
      null,
      "",
      true
    );
    setVideoUploading(false);

    if (res?.url) {
      setStepVideos((prev) => ({ ...prev, [stepType]: res.url }));
      addToast({
        message: `Video for ${activeSection.label} (${activeSection.step}) uploaded successfully!`,
        status: "success",
      });
      handleCancelPreUpload();
    } else {
      addToast({
        message: "Video upload failed. Please try again.",
        status: "error",
      });
    }
  };

  const handleVideoRemove = async () => {
    if (!window.confirm(`Remove the video for "${activeSection.label}"?`)) return;
    const stepType = activeTabType;
    const res = await fetchApi(`InductionPolicyConfig/step-video/${stepType}`, "DELETE");
    if (res) {
      setStepVideos((prev) => {
        const next = { ...prev };
        delete next[stepType];
        return next;
      });
      addToast({ message: "Video removed", status: "success" });
    }
  };

  // Load dropdown lists
  const loadDropdowns = async () => {
    const compRes = await fetchApi("Dropdown/company", "GET");
    if (compRes && Array.isArray(compRes)) {
      setCompanies(
        compRes.map((c: any) => ({
          ...c,
          id: c.company_ID || c.id,
          name: c.company_Name || c.name || `Company #${c.id}`,
        }))
      );
    }

    const deptRes = await fetchApi("DropDown/department", "GET");
    if (deptRes && Array.isArray(deptRes)) {
      setDepartments(
        deptRes.map((d: any) => ({
          ...d,
          id: d.id || d.department_Id,
          name: d.departmentName || d.name || `Dept #${d.id}`,
        }))
      );
    }

    const branchRes = await fetchApi("DropDown/branchesss", "GET");
    if (branchRes && Array.isArray(branchRes)) {
      setBranches(
        branchRes.map((b: any) => ({
          ...b,
          id: b.branch_Id || b.branchId || b.id,
          name: b.branchName || b.branch_Name || b.name || `Branch #${b.id}`,
        }))
      );
    }

    const bpRes = await fetchApi("DropDown/businessProcess", "GET");
    if (bpRes && Array.isArray(bpRes)) {
      setBusinessProcesses(
        bpRes.map((bp: any) => ({
          ...bp,
          id: bp.business_Process_Id || bp.id,
          name: bp.business_Process_Name || bp.name || `Vertical #${bp.id}`,
        }))
      );
    }
  };

  // Dynamic branch reload if single company selected
  useEffect(() => {
    if (selectedCompanies.length === 1) {
      fetchApi(`DropDown/roleWiseBranch?companyId=${selectedCompanies[0]}`, "GET").then((res) => {
        if (res && Array.isArray(res) && res.length > 0) {
          setBranches(
            res.map((b: any) => ({
              ...b,
              id: b.branch_Id || b.branchId || b.id,
              name: b.branchName || b.branch_Name || b.name,
            }))
          );
        }
      });
    }
  }, [selectedCompanies]);

  // Load table data filtered by active Tab Type
  const loadData = async () => {
    setLoading(true);
    let query = `first=${lazyParams.first}&rows=${lazyParams.rows}&configType=${activeTabType}`;

    if (filterCompany) query += `&companyId=${filterCompany}`;
    if (filterBranch) query += `&branchId=${filterBranch}`;
    if (filterDepartment) query += `&departmentId=${filterDepartment}`;
    if (filterBusinessProcess) query += `&businessProcessId=${filterBusinessProcess}`;
    if (searchTerm) query += `&globalFilter=${encodeURIComponent(searchTerm)}`;

    const response = await fetchApi(`InductionPolicyConfig?${query}`, "GET");
    if (response) {
      setTableData(response.data || []);
      setTotalCount(response.totalRecords || 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDropdowns();
    loadStepVideos();
  }, []);

  useEffect(() => {
    loadData();
  }, [lazyParams, filterCompany, filterBranch, filterDepartment, filterBusinessProcess, activeTabType, searchTerm,]);

  const getPresetsForType = (type: InductionConfigType) => {
    switch (type) {
      case "ORGANIZATION":
        return organizationPresets;
      case "BRANCH":
        return branchPresets;
      case "BUSINESS":
        return businessPresets;
      case "DEPARTMENT":
        return departmentPresets;
      case "POLICY":
        return policyPresets;
      default:
        return organizationPresets;
    }
  };

  const handleAddNew = () => {
    setIsEditMode(false);
    setSelectedData(null);
    setSelectedCompanies([]);
    setSelectedBranches([]);
    setSelectedDepartments([]);
    setSelectedBusinessProcesses([]);

    const initialType = activeTabType;
    const presets = getPresetsForType(initialType);
    const defaultPreset = presets[0];

    // Compute next unique order index for the active tab
    const existingOrders = tableData
      .filter((d: any) => (d.config_Type || activeTabType) === initialType)
      .map((d: any) => Number(d.order_Index) || 0);
    const nextOrder = existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 1;

    reset({
      ...emptyInductionPolicy,
      config_Type: initialType,
      policy_Key: defaultPreset ? defaultPreset.key : "",
      policy_Title: defaultPreset ? defaultPreset.title : "",
      policy_Content: defaultPreset ? defaultPreset.defaultContent : "",
      order_Index: nextOrder,
    });
    onOpen();
  };

  const onEdit = (rowData: any) => {
    setIsEditMode(true);
    setSelectedData(rowData);
    setSelectedCompanies(rowData.company_Id ? [rowData.company_Id] : []);
    setSelectedBranches(rowData.branch_Id ? [rowData.branch_Id] : []);
    setSelectedDepartments(rowData.department_Id ? [rowData.department_Id] : []);
    setSelectedBusinessProcesses(rowData.business_Process_Id ? [rowData.business_Process_Id] : []);

    reset({
      config_Type: (rowData.config_Type as InductionConfigType) || activeTabType,
      company_Id: rowData.company_Id || null,
      branch_Id: rowData.branch_Id || null,
      department_Id: rowData.department_Id || null,
      business_Process_Id: rowData.business_Process_Id || null,
      policy_Key: rowData.policy_Key || "",
      policy_Title: rowData.policy_Title || "",
      policy_Content: rowData.policy_Content || "",
      order_Index: rowData.order_Index || 1,
      is_Active: rowData.is_Active !== false,
    });

    onOpen();
  };

  const onDelete = async (rowData: any) => {
    if (!window.confirm(`Are you sure you want to delete "${rowData.policy_Title}"?`)) {
      return;
    }
    const response = await fetchApi(`InductionPolicyConfig/${rowData.id}`, "DELETE");
    if (response) {
      addToast({ message: "Configuration deleted successfully", status: "success" });
      loadData();
    }
  };

  const onToggleStatus = async (rowData: any) => {
    const response = await fetchApi(`InductionPolicyConfig/${rowData.id}/toggle-status`, "PUT");
    if (response) {
      addToast({
        message: `Status updated to ${!rowData.is_Active ? "Active" : "Inactive"}`,
        status: "success",
      });
      loadData();
    }
  };

  const handlePresetSelect = (presetKey: string) => {
    const currentPresets = getPresetsForType(watchedConfigType);
    const found = currentPresets.find((p) => p.key === presetKey);
    if (found) {
      setValue("policy_Key", found.key, { shouldValidate: true });
      setValue("policy_Title", found.title, { shouldValidate: true });
      setValue("policy_Content", found.defaultContent, { shouldValidate: true });
    }
  };

  const onInvalid = (formErrors: any) => {
    const errorKeys = Object.keys(formErrors);
    if (errorKeys.length > 0) {
      const firstError = formErrors[errorKeys[0]]?.message || "Please check the highlighted fields and fix errors.";
      addToast({
        message: String(firstError),
        status: "warning",
      });
    }
  };

  const onSubmit = async (data: IInductionPolicy) => {
    const targetType = (data.config_Type || activeTabType) as InductionConfigType;
    const currentOrder = Number(data.order_Index);

    // Validate: order index cannot be the same for any tab
    const duplicate = tableData.find(
      (item: any) =>
        (item.config_Type || activeTabType) === targetType &&
        Number(item.order_Index) === currentOrder &&
        (!isEditMode || item.id !== selectedData?.id)
    );

    if (duplicate) {
      addToast({
        message: `Order sequence #${currentOrder} is already in use by "${duplicate.policy_Title}" in this tab. Each card in this tab must have a unique order index.`,
        status: "warning",
      });
      return;
    }

    setLoading(true);

    const payload = {
      ...data,
      company_Id: selectedCompanies.length === 1 ? selectedCompanies[0] : null,
      branch_Id: selectedBranches.length === 1 ? selectedBranches[0] : null,
      department_Id: selectedDepartments.length === 1 ? selectedDepartments[0] : null,
      business_Process_Id:
        selectedBusinessProcesses.length === 1 ? selectedBusinessProcesses[0] : null,
      company_Ids: selectedCompanies,
      branch_Ids: selectedBranches,
      department_Ids: selectedDepartments,
      business_Process_Ids: selectedBusinessProcesses,
    };

    const url = isEditMode
      ? `InductionPolicyConfig/${selectedData.id}`
      : "InductionPolicyConfig";
    const method = isEditMode ? "PUT" : "POST";

    const response = await fetchApi(url, method, payload);
    setLoading(false);

    if (response) {
      addToast({
        message: isEditMode
          ? "Configuration updated successfully"
          : "Configuration saved successfully",
        status: "success",
      });
      onClose();
      loadData();
    }
  };

  const resetFilters = () => {
    setFilterCompany("");
    setFilterBranch("");
    setFilterDepartment("");
    setFilterBusinessProcess("");
    setSearchTerm("");
  };

  const hasActiveFilters =
    Boolean(filterCompany) ||
    Boolean(filterBranch) ||
    Boolean(filterDepartment) ||
    Boolean(filterBusinessProcess) ||
    Boolean(searchTerm);

  const getVideoPlayUrl = (videoValue: string, stepType: string) => {
    if (!videoValue) return "";
    if (videoValue.startsWith("blob:")) return videoValue;
    if (videoValue.includes("/step-video/play/") || videoValue.includes("/step-video/stream/")) {
      return videoValue;
    }
    const clean = videoValue.split("?")[0];
    const parts = clean.split("/");
    const fileName = parts[parts.length - 1];
    if (fileName && fileName.includes(".")) {
      return `${baseUrl}InductionPolicyConfig/step-video/play/${fileName}`;
    }
    return `${baseUrl}InductionPolicyConfig/step-video/stream/${stepType}`;
  };

  const activeStepVideo = stepVideos[activeTabType] || "";
  const activeStepVideoPlayUrl = getVideoPlayUrl(activeStepVideo, activeTabType);

  return (
    <MyDiv>
      <Box p={{ base: 4, md: 8 }} bg="#F8FAFC" minH="100vh">
        {/* Header Section */}
        <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap={4} mb={6}>
          <Box>
            <HStack spacing={2.5} align="center" mb={1}>
              <Text fontSize="22px" fontWeight="700" color="#1A202C" letterSpacing="-0.3px">
                Induction Content & Policies
              </Text>
              <Badge
                bg={BRAND_BG}
                color={BRAND}
                border={`1px solid ${BRAND_BORDER}`}
                fontSize="11px"
                fontWeight="700"
                px={2.5}
                py={0.5}
                borderRadius="6px"
              >
                {totalCount} Total
              </Badge>
            </HStack>
            <Text fontSize="14px" color="#64748B">
              Configure dynamic text, cards, guidelines, and compliance policies displayed during onboarding.
            </Text>
          </Box>
          <Button
            onClick={handleAddNew}
            bg={BRAND}
            color="white"
            _hover={{ bg: BRAND_HOVER }}
            _active={{ bg: BRAND_HOVER }}
            fontWeight="600"
            fontSize="14px"
            px={5}
            h="42px"
            borderRadius="8px"
            boxShadow="0 2px 4px rgba(0, 104, 56, 0.2)"
          >
            + Add Content
          </Button>
        </Flex>

        {/* Section Tabs */}
        <Box
          bg="white"
          p={1.5}
          borderRadius="10px"
          border="1px solid #E2E8F0"
          boxShadow="0 1px 3px rgba(0,0,0,0.04)"
          mb={6}
        >
          <Flex wrap="wrap" gap={1}>
            {SECTION_TABS.map((tab, idx) => {
              const isActive = activeTabIdx === idx;
              return (
                <Button
                  key={tab.type}
                  onClick={() => {
                    setActiveTabIdx(idx);
                    setLazyParams((p) => ({ ...p, first: 0 }));
                  }}
                  variant="ghost"
                  flex={{ base: "1 1 100%", sm: "1 1 auto" }}
                  h="44px"
                  px={4}
                  borderRadius="8px"
                  bg={isActive ? BRAND : "transparent"}
                  color={isActive ? "white" : "#4A5568"}
                  _hover={isActive ? { bg: BRAND_HOVER } : { bg: BRAND_BG, color: BRAND }}
                  justifyContent="flex-start"
                  transition="all 0.15s ease"
                >
                  <HStack spacing={2.5} align="center" w="100%">
                    <Badge
                      fontSize="10px"
                      fontWeight="700"
                      px={2}
                      py={0.5}
                      borderRadius="4px"
                      bg={isActive ? "rgba(255,255,255,0.22)" : "#EDF2F7"}
                      color={isActive ? "white" : "#4A5568"}
                    >
                      {tab.step}
                    </Badge>
                    <Text fontSize="13px" fontWeight={isActive ? "700" : "600"}>
                      {tab.label}
                    </Text>
                  </HStack>
                </Button>
              );
            })}
          </Flex>
        </Box>

        {/* Step Video Bar (Compact & Sleek - Keeps Filters & Table fully visible) */}
        <Box
          bg="white"
          px={5}
          py={3.5}
          borderRadius="12px"
          border="1px solid #E2E8F0"
          boxShadow="0 1px 3px rgba(0,0,0,0.04)"
          mb={4}
        >
          <Flex
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={3}
          >
            <HStack spacing={3} align="center">
              <Box
                w="38px"
                h="38px"
                borderRadius="10px"
                bg={activeStepVideo ? BRAND_BG : "#F1F5F9"}
                color={activeStepVideo ? BRAND : "#64748B"}
                display="flex"
                alignItems="center"
                justifyContent="center"
                border={`1px solid ${activeStepVideo ? BRAND_BORDER : "#E2E8F0"}`}
                flexShrink={0}
              >
                {activeStepVideo ? (
                  <CheckCircleIcon boxSize={4} color={BRAND} />
                ) : (
                  <AttachmentIcon boxSize={4} />
                )}
              </Box>

              <Box>
                <HStack spacing={2} align="center" wrap="wrap">
                  <Text fontSize="13px" fontWeight="700" color="#1A202C">
                    Step Video: {activeSection.step} · {activeSection.label}
                  </Text>
                  {activeStepVideo ? (
                    <Badge
                      fontSize="10px"
                      fontWeight="700"
                      px={2}
                      py={0.5}
                      borderRadius="4px"
                      bg="#DCFCE7"
                      color="#15803D"
                      border="1px solid #BBF7D0"
                    >
                      Active & Uploaded
                    </Badge>
                  ) : (
                    <Badge
                      fontSize="10px"
                      px={2}
                      py={0.5}
                      borderRadius="4px"
                      bg="#F1F5F9"
                      color="#64748B"
                    >
                      No Video Attached
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="11px" color="#64748B" mt={0.5}>
                  {activeStepVideo
                    ? "Video is ready for new employee induction. Click 'Watch Video' to view full preview without obscuring the table."
                    : `Upload an optional MP4, WebM, or OGG video (max ${MAX_VIDEO_MB} MB) to explain this step to new joiners.`}
                </Text>
              </Box>
            </HStack>

            <HStack spacing={2} flexShrink={0} alignSelf={{ base: "flex-end", md: "center" }}>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/ogg"
                style={{ display: "none" }}
                onChange={handleVideoPick}
              />

              {activeStepVideo ? (
                <>
                  <Button
                    size="sm"
                    leftIcon={<ViewIcon />}
                    bg={BRAND}
                    color="white"
                    _hover={{ bg: BRAND_HOVER }}
                    borderRadius="8px"
                    fontSize="12px"
                    fontWeight="600"
                    onClick={() => setIsWatchModalOpen(true)}
                  >
                    Watch Video
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<AttachmentIcon />}
                    variant="outline"
                    borderColor="#CBD5E1"
                    color="#334155"
                    _hover={{ bg: BRAND_BG, color: BRAND, borderColor: BRAND }}
                    borderRadius="8px"
                    fontSize="12px"
                    fontWeight="600"
                    isLoading={videoUploading}
                    loadingText="Uploading..."
                    onClick={() => videoInputRef.current?.click()}
                  >
                    Replace Video
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    borderRadius="8px"
                    fontSize="12px"
                    fontWeight="600"
                    leftIcon={<CloseIcon boxSize={2} />}
                    isDisabled={videoUploading}
                    onClick={handleVideoRemove}
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  leftIcon={<AttachmentIcon />}
                  bg={BRAND}
                  color="white"
                  _hover={{ bg: BRAND_HOVER }}
                  borderRadius="8px"
                  fontSize="12px"
                  fontWeight="600"
                  isLoading={videoUploading}
                  loadingText="Uploading..."
                  onClick={() => videoInputRef.current?.click()}
                >
                  Upload Video
                </Button>
              )}
            </HStack>
          </Flex>
        </Box>

        {/* Filters Card */}
        <Box
          bg="white"
          p={5}
          borderRadius="12px"
          border="1px solid #E2E8F0"
          boxShadow="0 1px 3px rgba(0,0,0,0.04)"
          mb={6}
        >
          <Grid
            templateColumns={{
              base: "1fr",
              md: "2fr 1fr 1fr 1fr 1fr auto",
            }}
            gap={3}
            alignItems="center"
          >
            {/* Search */}
            <GridItem>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="#94A3B8" />
                </InputLeftElement>
                <Input
                  placeholder="Search title, key, or content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  borderRadius="8px"
                  borderColor="#CBD5E1"
                  bg="#F8FAFC"
                  _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
                />
              </InputGroup>
            </GridItem>

            {/* Company Filter */}
            <GridItem>
              <Select
                size="sm"
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
                borderRadius="8px"
                borderColor="#CBD5E1"
                bg="#F8FAFC"
                _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
              >
                <option value="">All Companies</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </GridItem>

            {/* Branch Filter */}
            <GridItem>
              <Select
                size="sm"
                value={filterBranch}
                onChange={(e) => setFilterBranch(e.target.value)}
                borderRadius="8px"
                borderColor="#CBD5E1"
                bg="#F8FAFC"
                _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
              >
                <option value="">All Branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </GridItem>

            {/* Business Process Filter */}
            <GridItem>
              <Select
                size="sm"
                value={filterBusinessProcess}
                onChange={(e) => setFilterBusinessProcess(e.target.value)}
                borderRadius="8px"
                borderColor="#CBD5E1"
                bg="#F8FAFC"
                _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
              >
                <option value="">All Verticals</option>
                {businessProcesses.map((bp) => (
                  <option key={bp.id} value={bp.id}>
                    {bp.name}
                  </option>
                ))}
              </Select>
            </GridItem>

            {/* Department Filter */}
            <GridItem>
              <Select
                size="sm"
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                borderRadius="8px"
                borderColor="#CBD5E1"
                bg="#F8FAFC"
                _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
              >
                <option value="">All Departments</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            </GridItem>

            {/* Reset Button */}
            <GridItem>
              <Button
                size="sm"
                leftIcon={<RepeatIcon />}
                variant="outline"
                colorScheme="gray"
                onClick={resetFilters}
                isDisabled={!hasActiveFilters}
                borderRadius="8px"
                h="32px"
                fontSize="12px"
              >
                Reset
              </Button>
            </GridItem>
          </Grid>
        </Box>

        {/* Data Table */}
        <Box
          bg="white"
          borderRadius="12px"
          border="1px solid #E2E8F0"
          boxShadow="0 1px 3px rgba(0,0,0,0.04)"
          overflow="hidden"
        >
          <CustomTables
            value={tableData}
            totalRecords={totalCount}
            lazy={true}
            lazyParams={lazyParams}
            onLazyParamsChange={setLazyParams}
            loading={loading}
          >
            {/* Order Column */}
            <Column
              header="#"
              body={(rowData: any) => (
                <Badge
                  fontSize="11px"
                  fontWeight="700"
                  bg={BRAND_BG}
                  color={BRAND}
                  border={`1px solid ${BRAND_BORDER}`}
                  px={2}
                  py={0.5}
                  borderRadius="6px"
                >
                  #{rowData.order_Index || 1}
                </Badge>
              )}
            />

            {/* Tag / Key */}
            <Column
              header="Category Tag"
              body={(rowData: any) => (
                <Badge
                  fontSize="11px"
                  fontWeight="600"
                  px={2}
                  py={0.5}
                  borderRadius="6px"
                  bg="#F1F5F9"
                  color="#334155"
                  border="1px solid #E2E8F0"
                >
                  {rowData.policy_Key}
                </Badge>
              )}
            />

            {/* Title Column */}
            <Column
              header="Title / Heading"
              body={(rowData: any) => (
                <Box maxW="260px">
                  <Text fontSize="13px" fontWeight="600" color="#1A202C">
                    {rowData.policy_Title}
                  </Text>
                  <Text fontSize="11px" color="#94A3B8" mt={0.5}>
                    Updated {rowData.updated_At ? new Date(rowData.updated_At).toLocaleDateString() : "Default"}
                  </Text>
                </Box>
              )}
            />

            {/* Target Scope Column */}
            <Column
              header="Target Audience"
              body={(rowData: any) => {
                const parts: string[] = [];
                if (rowData.company_Name) parts.push(rowData.company_Name);
                if (rowData.branch_Name) parts.push(rowData.branch_Name);
                if (rowData.business_Process_Name) parts.push(rowData.business_Process_Name);
                if (rowData.department_Name) parts.push(rowData.department_Name);

                if (parts.length === 0) {
                  return (
                    <Badge fontSize="11px" px={2} py={0.5} borderRadius="6px" bg="#F8FAFC" color="#64748B" border="1px solid #E2E8F0">
                      Global (All Employees)
                    </Badge>
                  );
                }

                return (
                  <Badge fontSize="11px" px={2} py={0.5} borderRadius="6px" bg="#EFF6FF" color="#1E40AF" border="1px solid #DBEAFE">
                    {parts.join(" · ")}
                  </Badge>
                );
              }}
            />

            {/* Content Preview Column */}
            <Column
              header="Content Snippet"
              body={(rowData: any) => (
                <Tooltip label={rowData.policy_Content} placement="top" hasArrow>
                  <Text noOfLines={2} fontSize="12px" lineHeight={1.5} color="#475569" maxW="340px">
                    {rowData.policy_Content}
                  </Text>
                </Tooltip>
              )}
            />

            {/* Status Column */}
            <Column
              header="Active"
              body={(rowData: any) => (
                <Switch
                  size="sm"
                  colorScheme="green"
                  isChecked={rowData.is_Active !== false}
                  onChange={() => onToggleStatus(rowData)}
                />
              )}
            />

            {/* Actions Column */}
            <Column
              header="Actions"
              body={(rowData: any) => (
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Edit"
                    icon={<EditIcon />}
                    size="sm"
                    variant="ghost"
                    color="#475569"
                    _hover={{ bg: BRAND_BG, color: BRAND }}
                    onClick={() => onEdit(rowData)}
                  />
                  <IconButton
                    aria-label="Delete"
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    color="#EF4444"
                    _hover={{ bg: "#FEF2F2" }}
                    onClick={() => onDelete(rowData)}
                  />
                </HStack>
              )}
            />
          </CustomTables>
        </Box>

        {/* Drawer for Add/Edit */}
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
          <DrawerOverlay />
          <DrawerContent maxW={{ base: "100%", md: "640px" }} bg="#FFFFFF">
            <DrawerHeader
              borderBottom="1px solid #E2E8F0"
              p={6}
              bg="#FAFAFA"
            >
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="16px" fontWeight="700" color="#1A202C">
                    {isEditMode ? "Edit Induction Content" : "Add Induction Content"}
                  </Text>
                  <Text fontSize="12px" color="#64748B" mt={0.5}>
                    {activeSection.description}
                  </Text>
                </Box>
                <Badge
                  fontSize="11px"
                  fontWeight="700"
                  px={2.5}
                  py={1}
                  borderRadius="6px"
                  bg={BRAND_BG}
                  color={BRAND}
                  border={`1px solid ${BRAND_BORDER}`}
                >
                  {activeSection.step}: {activeSection.label}
                </Badge>
              </Flex>
            </DrawerHeader>

            <DrawerBody p={6}>
              <Box as="form" id="induction-content-form" onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
                {/* 1. Target Section Selector */}
                <Box mb={5}>
                  <FormLabel fontSize="12px" fontWeight="700" color="#334155" textTransform="uppercase" letterSpacing="0.4px" mb={1.5}>
                    Target Induction Step
                  </FormLabel>
                  <Controller
                    name="config_Type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        size="sm"
                        value={field.value}
                        borderRadius="8px"
                        borderColor="#CBD5E1"
                        _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
                        onChange={(e: any) => {
                          const val = e.target.value as InductionConfigType;
                          field.onChange(val);
                          const currentPresets = getPresetsForType(val);
                          if (currentPresets.length > 0) {
                            setValue("policy_Key", currentPresets[0].key);
                            setValue("policy_Title", currentPresets[0].title);
                            setValue("policy_Content", currentPresets[0].defaultContent);
                          }
                          if (!isEditMode) {
                            const existingOrders = tableData
                              .filter((d: any) => (d.config_Type || activeTabType) === val)
                              .map((d: any) => Number(d.order_Index) || 0);
                            const nextOrder = existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 1;
                            setValue("order_Index", nextOrder);
                          }
                        }}
                      >
                        {SECTION_TABS.map((t) => (
                          <option key={t.type} value={t.type}>
                            {t.step} · {t.label}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </Box>

                {/* 2. Quick Template Presets */}
                {!isEditMode && (
                  <Box mb={5} p={4} bg="#F8FAFC" borderRadius="10px" border="1px dashed #CBD5E1">
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontSize="12px" fontWeight="700" color="#334155" textTransform="uppercase" letterSpacing="0.4px">
                        Template Presets
                      </Text>
                      <Text fontSize="11px" color="#64748B">
                        Select to auto-fill recommended text
                      </Text>
                    </Flex>
                    <Select
                      placeholder="Select a standard template..."
                      size="sm"
                      bg="white"
                      borderColor="#CBD5E1"
                      borderRadius="8px"
                      _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
                      onChange={(e) => handlePresetSelect(e.target.value)}
                    >
                      {getPresetsForType(watchedConfigType).map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.title}
                        </option>
                      ))}
                    </Select>
                  </Box>
                )}

                {/* 3. Scoping Controls (Who sees this?) */}
                <Box mb={5} p={4} bg="#FFFFFF" borderRadius="10px" border="1px solid #E2E8F0">
                  <Text fontSize="12px" fontWeight="700" color="#334155" textTransform="uppercase" letterSpacing="0.4px" mb={1}>
                    Target Audience Scoping
                  </Text>
                  <Text fontSize="11px" color="#64748B" mb={3}>
                    Leave all empty to apply as a global default for all employees. Select specific items to target.
                  </Text>

                  <Grid templateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={3}>
                    <FormControl>
                      <FormLabel fontSize="12px" fontWeight="600" color="#475569" mb={1}>
                        Company
                      </FormLabel>
                      <MultiSelectMenu
                        options={companies}
                        selectedValues={selectedCompanies}
                        setSelectedValues={setSelectedCompanies}
                        labelKey="name"
                        valueKey="id"
                        placeholder="All Companies"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="12px" fontWeight="600" color="#475569" mb={1}>
                        Branch
                      </FormLabel>
                      <MultiSelectMenu
                        options={branches}
                        selectedValues={selectedBranches}
                        setSelectedValues={setSelectedBranches}
                        labelKey="name"
                        valueKey="id"
                        placeholder="All Branches"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="12px" fontWeight="600" color="#475569" mb={1}>
                        Business Vertical
                      </FormLabel>
                      <MultiSelectMenu
                        options={businessProcesses}
                        selectedValues={selectedBusinessProcesses}
                        setSelectedValues={setSelectedBusinessProcesses}
                        labelKey="name"
                        valueKey="id"
                        placeholder="All Verticals"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="12px" fontWeight="600" color="#475569" mb={1}>
                        Department
                      </FormLabel>
                      <MultiSelectMenu
                        options={departments}
                        selectedValues={selectedDepartments}
                        setSelectedValues={setSelectedDepartments}
                        labelKey="name"
                        valueKey="id"
                        placeholder="All Departments"
                      />
                    </FormControl>
                  </Grid>
                </Box>

                {/* 4. Category Key & Title */}
                <Grid templateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={3} mb={4}>
                  <FormControl isRequired isInvalid={!!errors.policy_Key}>
                    <FormLabel fontSize="12px" fontWeight="600" color="#334155" mb={1}>
                      Category Tag / Identifier
                    </FormLabel>
                    <Controller
                      name="policy_Key"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          size="sm"
                          borderRadius="8px"
                          borderColor={errors.policy_Key ? "red.400" : "#CBD5E1"}
                          _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
                          placeholder="e.g. Vision, Facilities, Leave Policy"
                        />
                      )}
                    />
                    <FormErrorMessage fontSize="11px" fontWeight="600" mt={1}>
                      {errors.policy_Key?.message}
                    </FormErrorMessage>
                    {!errors.policy_Key && (
                      <Text fontSize="11px" color="#94A3B8" mt={1}>
                        Identifier used to position or group cards
                      </Text>
                    )}
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.policy_Title}>
                    <FormLabel fontSize="12px" fontWeight="600" color="#334155" mb={1}>
                      Display Title
                    </FormLabel>
                    <Controller
                      name="policy_Title"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          size="sm"
                          borderRadius="8px"
                          borderColor={errors.policy_Title ? "red.400" : "#CBD5E1"}
                          _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
                          placeholder="e.g. Branch Facilities & Amenities"
                        />
                      )}
                    />
                    <FormErrorMessage fontSize="11px" fontWeight="600" mt={1}>
                      {errors.policy_Title?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Grid>

                {/* 5. Policy Content */}
                <FormControl isRequired isInvalid={!!errors.policy_Content} mb={4}>
                  <Flex justify="space-between" align="center" mb={1}>
                    <FormLabel fontSize="12px" fontWeight="600" color="#334155" mb={0}>
                      Content / Guidelines Text
                    </FormLabel>
                    <Text fontSize="11px" color={watchedPolicyContent.length > 4000 ? "red.500" : "#94A3B8"}>
                      {watchedPolicyContent.length} / 4000
                    </Text>
                  </Flex>
                  <Controller
                    name="policy_Content"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        rows={5}
                        fontSize="13px"
                        lineHeight={1.6}
                        borderRadius="8px"
                        borderColor={errors.policy_Content ? "red.400" : "#CBD5E1"}
                        _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
                        placeholder="Enter the complete text or guidelines to be displayed and narrated to the employee..."
                      />
                    )}
                  />
                  <FormErrorMessage fontSize="11px" fontWeight="600" mt={1}>
                    {errors.policy_Content?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* 6. Order Index & Status */}
                <Grid templateColumns="1fr 1fr" gap={3} mb={5}>
                  <FormControl isRequired isInvalid={!!errors.order_Index}>
                    <FormLabel fontSize="12px" fontWeight="600" color="#334155" mb={1}>
                      Display Sequence (#) <Text as="span" fontSize="11px" color="#64748B" fontWeight="normal">(Unique per tab)</Text>
                    </FormLabel>
                    <Controller
                      name="order_Index"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="number"
                          min={1}
                          max={999}
                          size="sm"
                          borderRadius="8px"
                          borderColor={errors.order_Index ? "red.400" : "#CBD5E1"}
                          _focus={{ bg: "white", borderColor: BRAND, boxShadow: `0 0 0 1px ${BRAND}` }}
                          value={field.value}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                    <FormErrorMessage fontSize="11px" fontWeight="600" mt={1}>
                      {errors.order_Index?.message}
                    </FormErrorMessage>
                    {!errors.order_Index && (
                      <Text fontSize="11px" color="#64748B" mt={1}>
                        Must be unique for this tab (cannot be duplicate)
                      </Text>
                    )}
                  </FormControl>

                  <FormControl display="flex" alignItems="center" pt={6}>
                    <Controller
                      name="is_Active"
                      control={control}
                      render={({ field }) => (
                        <HStack spacing={3}>
                          <Switch
                            id="drawer-active-switch"
                            colorScheme="green"
                            isChecked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                          <FormLabel htmlFor="drawer-active-switch" fontSize="13px" fontWeight="600" color="#334155" mb={0}>
                            Active & Visible
                          </FormLabel>
                        </HStack>
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* 7. Live Preview Card */}
                <Box p={4} bg="#F8FAFC" borderRadius="10px" border="1px solid #E2E8F0">
                  <Text fontSize="11px" fontWeight="700" color="#64748B" textTransform="uppercase" letterSpacing="0.4px" mb={2}>
                    Live Employee Preview
                  </Text>
                  <Box p={4} bg="white" borderRadius="8px" border={`1px solid ${BRAND_BORDER}`} boxShadow="sm">
                    <HStack justify="space-between" mb={1.5}>
                      <Text fontSize="14px" fontWeight="600" color="#1A202C">
                        {watchedPolicyTitle || "Card Title"}
                      </Text>
                      <Badge fontSize="10px" px={2} py={0.5} borderRadius="4px" bg={BRAND_BG} color={BRAND} border={`1px solid ${BRAND_BORDER}`}>
                        {watchedPolicyKey || "Tag"}
                      </Badge>
                    </HStack>
                    <Text fontSize="13px" lineHeight={1.5} color="#475569">
                      {watchedPolicyContent || "Your configured text will appear here cleanly."}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </DrawerBody>

            <DrawerFooter borderTop="1px solid #E2E8F0" p={4} bg="#FAFAFA">
              <Button variant="ghost" size="sm" mr={3} onClick={onClose} borderRadius="8px">
                Cancel
              </Button>
              <Button
                type="submit"
                form="induction-content-form"
                size="sm"
                bg={BRAND}
                color="white"
                _hover={{ bg: BRAND_HOVER }}
                _active={{ bg: BRAND_HOVER }}
                borderRadius="8px"
                px={5}
                isLoading={loading}
                boxShadow="0 2px 4px rgba(0, 104, 56, 0.2)"
              >
                {isEditMode ? "Save Changes" : "Create Content"}
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        {/* Watch Uploaded Step Video Modal */}
        <Modal
          isOpen={isWatchModalOpen}
          onClose={() => setIsWatchModalOpen(false)}
          size="2xl"
          isCentered
        >
          <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(3px)" />
          <ModalContent borderRadius="14px" overflow="hidden">
            <ModalHeader
              bg="#F8FAFC"
              borderBottom="1px solid #E2E8F0"
              py={3.5}
              px={5}
            >
              <Flex justify="space-between" align="center" pr={6}>
                <HStack spacing={2}>
                  <Text fontSize="15px" fontWeight="700" color="#1A202C">
                    {activeSection.step}: {activeSection.label}
                  </Text>
                  <Badge
                    fontSize="11px"
                    fontWeight="700"
                    px={2}
                    py={0.5}
                    borderRadius="4px"
                    bg={BRAND_BG}
                    color={BRAND}
                    border={`1px solid ${BRAND_BORDER}`}
                  >
                    Induction Video
                  </Badge>
                </HStack>
              </Flex>
            </ModalHeader>
            <ModalCloseButton top={3} />
            <ModalBody p={0} bg="black">
              {activeStepVideo && (
                <Box
                  as="video"
                  key={activeStepVideoPlayUrl}
                  src={activeStepVideoPlayUrl}
                  controls
                  autoPlay
                  w="100%"
                  maxH="480px"
                  display="block"
                  bg="black"
                />
              )}
            </ModalBody>
            <ModalFooter bg="#F8FAFC" borderTop="1px solid #E2E8F0" py={3} px={5} justifyContent="space-between">
              <Text fontSize="12px" color="#64748B">
                Video is attached to {activeSection.label} and shown during employee onboarding.
              </Text>
              <Button
                size="sm"
                borderRadius="8px"
                onClick={() => setIsWatchModalOpen(false)}
              >
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Pre-Upload Video Preview Confirmation Modal */}
        <Modal
          isOpen={isPreUploadModalOpen}
          onClose={handleCancelPreUpload}
          size="xl"
          isCentered
          closeOnOverlayClick={!videoUploading}
        >
          <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(3px)" />
          <ModalContent borderRadius="14px" overflow="hidden">
            <ModalHeader
              bg="#F8FAFC"
              borderBottom="1px solid #E2E8F0"
              py={3.5}
              px={5}
            >
              <Flex justify="space-between" align="center" pr={6}>
                <Box>
                  <Text fontSize="15px" fontWeight="700" color="#1A202C">
                    Confirm Video Upload
                  </Text>
                  <Text fontSize="12px" color="#64748B">
                    Preview your selected video before uploading to the server
                  </Text>
                </Box>
                <Badge
                  fontSize="11px"
                  fontWeight="700"
                  px={2}
                  py={0.5}
                  borderRadius="4px"
                  bg={BRAND_BG}
                  color={BRAND}
                  border={`1px solid ${BRAND_BORDER}`}
                >
                  {activeSection.step}: {activeSection.label}
                </Badge>
              </Flex>
            </ModalHeader>
            {!videoUploading && <ModalCloseButton top={3} />}
            <ModalBody p={5}>
              {pendingVideoPreviewUrl && (
                <Box
                  as="video"
                  src={pendingVideoPreviewUrl}
                  controls
                  w="100%"
                  maxH="300px"
                  borderRadius="10px"
                  bg="black"
                  mb={4}
                  boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                />
              )}

              {/* File details card */}
              <Box
                p={3.5}
                bg="#F8FAFC"
                borderRadius="10px"
                border="1px solid #E2E8F0"
              >
                <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                  <Box>
                    <Text fontSize="11px" fontWeight="600" color="#64748B" textTransform="uppercase">
                      File Name
                    </Text>
                    <Text fontSize="12px" fontWeight="600" color="#1E293B" noOfLines={1} title={pendingVideoFile?.name}>
                      {pendingVideoFile?.name || "—"}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="11px" fontWeight="600" color="#64748B" textTransform="uppercase">
                      File Size
                    </Text>
                    <Text fontSize="12px" fontWeight="600" color="#1E293B">
                      {pendingVideoFile ? `${(pendingVideoFile.size / (1024 * 1024)).toFixed(2)} MB` : "—"}
                    </Text>
                  </Box>
                  <Box mt={1}>
                    <Text fontSize="11px" fontWeight="600" color="#64748B" textTransform="uppercase">
                      Format
                    </Text>
                    <Text fontSize="12px" fontWeight="600" color="#1E293B">
                      {pendingVideoFile?.type || "video/mp4"}
                    </Text>
                  </Box>
                  <Box mt={1}>
                    <Text fontSize="11px" fontWeight="600" color="#64748B" textTransform="uppercase">
                      Destination Step
                    </Text>
                    <Text fontSize="12px" fontWeight="600" color={BRAND}>
                      {activeSection.step}: {activeSection.label}
                    </Text>
                  </Box>
                </Grid>
              </Box>

              {/* Audience Scoping MultiSelect Controls */}
              <Box mt={3.5} p={3.5} bg="#FFFFFF" borderRadius="10px" border="1px solid #E2E8F0">
                <Text fontSize="11px" fontWeight="700" color="#334155" textTransform="uppercase" letterSpacing="0.4px" mb={0.5}>
                  Audience Scoping (MultiSelect)
                </Text>
                <Text fontSize="11px" color="#64748B" mb={3}>
                  Leave empty to apply as a global default video for all employees, or select specific target audience.
                </Text>

                <Grid templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }} gap={2.5}>
                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="600" color="#475569" mb={1}>
                      Company
                    </FormLabel>
                    <MultiSelectMenu
                      options={companies}
                      selectedValues={videoTargetCompanies}
                      setSelectedValues={setVideoTargetCompanies}
                      labelKey="name"
                      valueKey="id"
                      placeholder="All Companies"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="600" color="#475569" mb={1}>
                      Branch
                    </FormLabel>
                    <MultiSelectMenu
                      options={branches}
                      selectedValues={videoTargetBranches}
                      setSelectedValues={setVideoTargetBranches}
                      labelKey="name"
                      valueKey="id"
                      placeholder="All Branches"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="600" color="#475569" mb={1}>
                      Department
                    </FormLabel>
                    <MultiSelectMenu
                      options={departments}
                      selectedValues={videoTargetDepartments}
                      setSelectedValues={setVideoTargetDepartments}
                      labelKey="name"
                      valueKey="id"
                      placeholder="All Departments"
                    />
                  </FormControl>
                </Grid>
              </Box>

              <HStack spacing={2} mt={3} p={2.5} bg="#EFF6FF" borderRadius="8px" border="1px solid #BFDBFE">
                <InfoOutlineIcon color="#2563EB" boxSize={3.5} flexShrink={0} />
                <Text fontSize="11px" color="#1E40AF">
                  Once uploaded, this video will be presented to new employees during {activeSection.label}. If a video is already assigned, it will be replaced.
                </Text>
              </HStack>
            </ModalBody>
            <ModalFooter bg="#F8FAFC" borderTop="1px solid #E2E8F0" py={3} px={5}>
              <HStack spacing={3}>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="#CBD5E1"
                  color="#475569"
                  borderRadius="8px"
                  isDisabled={videoUploading}
                  onClick={handleCancelPreUpload}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  bg={BRAND}
                  color="white"
                  _hover={{ bg: BRAND_HOVER }}
                  borderRadius="8px"
                  isLoading={videoUploading}
                  loadingText="Uploading Video..."
                  onClick={handleConfirmVideoUpload}
                >
                  Confirm & Upload
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </MyDiv>
  );
} 